import type { CSSProperties, JSX } from 'react';
import type { Palette } from '../../../../_theme/palette.js';
import { hexWithAlpha } from '../../../../_theme/palette.js';
import type { SurveyQuestion } from '../../types.js';

interface SliderFieldProps {
  palette: Palette;
  question: SurveyQuestion;
  value: unknown;
  onChange: (value: number) => void;
}

// Native HTML range slider styled to use the survey accent color via
// `accent-color` (modern browsers) plus a tinted track via gradient that
// updates as the user drags. Min/max labels under the rail mirror the
// main app's SliderPreview.
export function SliderField({ palette, question, value, onChange }: SliderFieldProps): JSX.Element {
  const min = question.settings?.min ?? 0;
  const max = question.settings?.max ?? 100;
  const step = question.settings?.step ?? 1;
  const current = typeof value === 'number' ? value : question.settings?.defaultValue ?? Math.round((min + max) / 2);
  const ratio = max > min ? (current - min) / (max - min) : 0;
  const fillPercent = Math.round(Math.max(0, Math.min(1, ratio)) * 100);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={current}
        onChange={(event) => onChange(Number(event.currentTarget.value))}
        style={rangeStyle(palette, fillPercent)}
        aria-label={question.label}
        aria-valuenow={current}
        aria-valuemin={min}
        aria-valuemax={max}
      />
      <div style={labelRowStyle(palette)}>
        <span>{question.settings?.lowLabel ?? min}</span>
        <span style={{ fontWeight: 600, color: palette.text, fontSize: 13 }}>{current}</span>
        <span>{question.settings?.highLabel ?? max}</span>
      </div>
    </div>
  );
}

function rangeStyle(palette: Palette, fillPercent: number): CSSProperties {
  // Linear gradient creates a colored track up to the thumb, mirroring the
  // shadcn Slider component the main app uses. accent-color handles the
  // thumb tint on browsers that support it.
  return {
    width: '100%',
    accentColor: palette.primary,
    cursor: 'pointer',
    appearance: 'none',
    height: 6,
    background: `linear-gradient(to right, ${palette.primary} 0%, ${palette.primary} ${fillPercent}%, ${hexWithAlpha(palette.primary, 0.15)} ${fillPercent}%, ${hexWithAlpha(palette.primary, 0.15)} 100%)`,
    borderRadius: 999,
    outline: 'none',
  };
}

function labelRowStyle(palette: Palette): CSSProperties {
  return {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: 12,
    color: palette.textMuted,
  };
}
